import { useGetRedlistedApplicantsQuery } from "@/app/features/recruitment/hiring-pipeline/hiring-pipeline.api";
import CustomModal from "@/components/CustomModal";
import EmptyState from "@/components/EmptyState";
import ViewToggler from "@/components/ViewToggler";
import WithLoader from "@/components/WithLoader";
import { FILTER_KEYS } from "@/constants/keys.constants";
import { useAppFilters } from "@/hooks";
import useModal from "@/hooks/useModal";
import {
  FileTextIcon,
  PaperclipIcon,
  SearchIcon,
  UserMultipleIcon,
} from "@/icons";
import { Flex, Input, Typography } from "antd";
import EmpCard from "../Evaluation/_components/EvAssessment/EmpCard";

const { Text } = Typography;

export default function RedListed() {
  const { skip, limit, keyword, handleSearch } = useAppFilters(
    FILTER_KEYS.HIRING_PIPELINE,
  );

  const {
    open: reasonModal,
    handleOpen: openReason,
    handleClose: closeReason,
  } = useModal<{ applicant_name: string; reason: string | null }>();

  // ── Options ────────────────────────────────────────────────────────────────
  // const [jobOptions, isJobsFetching] = useSelectionOptions(
  //   useGetAllJobPostsQuery,
  //   {
  //     labelKey: "job_title",
  //     valueKey: "id",
  //   },
  // );

  // const [departmentOptions, isDeptFetching] = useSelectionOptions(
  //   useGetAllDepartmentsQuery,
  //   {
  //     labelKey: "name",
  //     valueKey: "id",
  //   },
  // );

  // ── Data ───────────────────────────────────────────────────────────────────
  const {
    data: redListedData,
    isLoading,
    isFetching,
  } = useGetRedlistedApplicantsQuery({
    params: {
      skip,
      limit,
      search: keyword,
    },
  });

  // const [bulkRedlist] = useBulkRedlistApplicantMutation();

  const applicants = redListedData?.data?.data || [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Flex
        align="start"
        justify="space-between"
        gap={16}
        className="flex-wrap lg:flex-nowrap"
      >
        <ViewToggler disabled="list" mode="grid" setMode={() => {}} />
        <Flex vertical gap={12} align="end">
          <Flex gap={16} align="center" className="flex-wrap lg:flex-nowrap">
            <Input
              allowClear
              size="large"
              onChange={handleSearch}
              placeholder="Search Applicant"
              prefix={<SearchIcon size={20} />}
            />
            {/* <div>
              <Select
                allowClear
                showSearch
                size="large"
                placement="bottomRight"
                optionFilterProp="label"
                placeholder="Job Position"
                popupMatchSelectWidth={false}
                loading={isJobsFetching as boolean}
                onChange={(value) => handleFilter("job_id", value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={jobOptions as any[]}
              />
            </div>
            <div>
              <Select
                allowClear
                showSearch
                size="large"
                placement="bottomRight"
                optionFilterProp="label"
                placeholder="Department"
                popupMatchSelectWidth={false}
                loading={isDeptFetching as boolean}
                onChange={(value) => handleFilter("department_id", value)}
                suffixIcon={
                  <ChevronDownIcon size={24} className="text-white" />
                }
                options={departmentOptions as any[]}
              />
            </div> */}
          </Flex>
        </Flex>
      </Flex>

      {/* Cards */}
      <WithLoader isLoading={isLoading || isFetching}>
        {applicants.length === 0 ? (
          <EmptyState
            icon={<UserMultipleIcon size={40} />}
            title="No Red Listed Applicants"
            description="No applicants have been red listed yet, or none match your current filters."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {applicants.map((u) => (
              <EmpCard
                key={u.id}
                showStatus={false}
                item={{
                  name: u.applicant?.name ?? u.email ?? "Unknown",
                  photo: u.applicant?.photo ?? null,
                  designation: u.applicant?.nid ?? u.nid ?? "",
                  department: "",
                  assessment_status: undefined as any,
                }}
                actionRender={
                  <div className="space-y-2">
                    <button
                      onClick={() =>
                        openReason({
                          data: {
                            applicant_name:
                              u.applicant?.name ?? u.email ?? "Unknown",
                            reason: u.reason,
                          },
                        })
                      }
                      className="text-theme-primary font-semibold cursor-pointer underline underline-offset-2"
                    >
                      Reason
                    </button>
                    {u.applicant?.cv_link && (
                      <a
                        href={u.applicant.cv_link}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-center gap-1 w-full p-1 bg-theme-gray-90 rounded text-theme-slate-800 text-sm"
                      >
                        <PaperclipIcon size={14} className="text-[#007FC4]" />
                        {u.applicant.cv_link.split("/").pop()?.slice(0, 8)}
                        ...pdf
                      </a>
                    )}
                  </div>
                }
              />
            ))}
          </div>
        )}
      </WithLoader>

      <CustomModal
        open={reasonModal.open}
        handleClose={closeReason}
        size={500}
        title="Red List Reason"
      >
        <div className="space-y-4 pt-4">
          <div className="space-y-1">
            <Text className="text-theme-slate-400 text-base font-semibold block">
              Applicant Name
            </Text>
            <Text className="text-white text-lg">
              {reasonModal.data?.applicant_name}
            </Text>
          </div>

          <div className="space-y-1">
            <Text className="text-theme-slate-400 text-base font-semibold block">
              Reason for Red List
            </Text>
            <div className="bg-theme-slate-900/50 p-4 rounded-xl border border-theme-gray-700 mt-2">
              <Text className="text-white text-[15px] leading-relaxed block">
                {reasonModal.data?.reason || "No specific reason provided."}
              </Text>
            </div>
          </div>

          <div className="flex justify-center pt-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-theme-primary/10">
              <FileTextIcon size={24} className="text-theme-primary" />
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
